import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IBraze } from 'braze-web-sdk';

import { useContentCardsNotificationsExperiment } from 'experiments/use-content-cards-notifications';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import useEffectOnce from 'hooks/use-effect-once';
import useReadyQueue from 'hooks/use-ready-queue';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import Braze, { ContentCard } from 'utils/braze';
import { getApiKey, getConfigValue, region } from 'utils/environment';
import { Region } from 'utils/environment/types';
import logger from 'utils/logger';
import noop from 'utils/noop';

import { IBrazeContext, WebContentCard } from '../types';

import { filterContentCardsByFeed, filterContentCardsByRules } from './utils';

const useBraze = () => {
  const dispatch = useAppDispatch();
  const {
    isNewsFeedEnabled,
    isHomePageFeedEnabled,
    isExperimentEnabled: enableContentCards,
  } = useContentCardsNotificationsExperiment();
  const { enqueueIfNotDrained, drainQueue } = useReadyQueue();
  const brazeKey = getApiKey({ key: 'braze' });
  const brazeUrl = getConfigValue({ key: 'braze', defaultValue: {} }).initUrl;
  const webContentCards = useAppSelector(selectors.loyalty.selectWebContentCards);
  const [isBrazeInitialized, setIsBrazeInitialized] = useState(false);
  const [activeDayParts, setActiveDayParts] = useState<IDayPartBoundary[]>([]);

  const toggleContentCards = useCallback<IBrazeContext['toggleContentCards']>(
    enqueueIfNotDrained((parentNode: Element) => {
      // @ts-ignore TS is reading types from mobile file. Signatures are different compared with Braze.web
      Braze.launchContentCards(parentNode);
    }),
    [enqueueIfNotDrained]
  );

  const setUserId = useCallback<IBrazeContext['setUserId']>(
    enqueueIfNotDrained((userId: string | null) => {
      // @ts-ignore TS is reading types from mobile file. Signatures are different compared with Braze.web
      Braze.changeUser(userId);
      return Braze.requestContentCardsRefresh();
    }),
    []
  );

  // Making this async to be compatible with the react native module
  const getCachedContentCards = useCallback<IBrazeContext['getCachedContentCards']>(async () => {
    // @ts-ignore TS is reading types from mobile file. Signatures are different compared with Braze.web
    const { cards = [] } = Braze.getContentCards() || {};
    return cards as ContentCard[];
  }, []);

  const logContentCardImpression = useCallback<IBrazeContext['logContentCardImpression']>(
    async (cardId: string) => {
      const card = webContentCards.find(({ id }) => id === cardId);
      if (card) {
        // This casting must be done because the signature of logContentCardImpression for web
        // is different from the one for the native SDK.
        return Braze.logContentCardImpression(([card] as unknown) as string);
      }
    },
    [webContentCards]
  );

  const logContentCardClicked = useCallback<IBrazeContext['logContentCardClicked']>(
    (card: ContentCard, logClickEvent: boolean = true) => {
      const webCard = webContentCards.find(({ id }) => id === card.id);

      if (logClickEvent) {
        // log event to amplitude
        const eventAttributes = {
          marketingCardUrl: card.url,
          ...('title' in card && { marketingTileName: card.title }), // ImageOnly cards have no title
          ...(card.extras || {}),
        };
        logEvent(CustomEventNames.MARKETING_TILE_CLICK, EventTypes.Navigation, eventAttributes);
      }

      if (webCard) {
        // This casting must be done because the signature of logContentCardImpression for web
        // is different from the one for the native SDK.
        return Braze.logContentCardClicked((webCard as unknown) as string);
      }
    },
    [webContentCards]
  );

  const setContentCardValues = useCallback(
    (webContentCards: WebContentCard[]) => {
      if (!activeDayParts.length) {
        return;
      }
      const availableContentCards = filterContentCardsByRules(webContentCards, activeDayParts);

      const filteredCards = filterContentCardsByFeed({
        cards: availableContentCards,
        isNewsFeedEnabled,
        isHomePageFeedEnabled,
      });
      dispatch(actions.loyalty.setWebContentCards(filteredCards));
    },
    [activeDayParts, dispatch, isHomePageFeedEnabled, isNewsFeedEnabled]
  );

  const initBrazeRef = useRef(noop);
  const initWasCalled = useRef(false);

  initBrazeRef.current = () => {
    import('../init')
      .then(({ default: init }) => {
        if (initWasCalled.current) {
          return;
        }
        init(
          brazeKey,
          brazeUrl,
          () => {
            drainQueue();
            setIsBrazeInitialized(true);
          },
          {
            // currently not enabled for CA
            enableHtmlInAppMessagesInBrazeSdkConfig: region === Region.US,
          }
        );
        initWasCalled.current = true;
      })
      .catch(error => {
        logger.error({ error, message: 'Braze: import error' });
      });
  };

  // TODO: check what should happen when the deps changes. Should this hook act reactively?
  useEffectOnce(() => {
    initBrazeRef.current?.();
  });

  useEffectOnUnmount(() => {
    // braze requests are batched and flushed.
    // if the app is unmounting we should immediately
    // send anything in the queue
    Braze.requestImmediateDataFlush();
  });

  const loadContentCards = useCallback(async () => {
    const cachedContentCards = await getCachedContentCards();
    if (cachedContentCards.length > 0) {
      setContentCardValues((cachedContentCards as unknown) as WebContentCard[]);
    }

    window.braze.subscribeToContentCardsUpdates(update => {
      setContentCardValues((update.cards as unknown) as WebContentCard[]);
    });
    Braze.requestContentCardsRefresh();
  }, [getCachedContentCards, setContentCardValues]);

  useEffect(() => {
    if (!enableContentCards || !isBrazeInitialized) {
      return;
    }
    loadContentCards();
  }, [enableContentCards, isBrazeInitialized, loadContentCards]);

  const ctxValue: IBrazeContext = useMemo(
    () => ({
      initBraze: initBrazeRef.current,
      getCachedContentCards,
      setUserId,
      toggleContentCards,
      isInAppMessageOpen: false,
      setIsInAppMessageOpen: noop,
      logContentCardImpression,
      logContentCardClicked,
      setActiveDayParts,
    }),
    [
      getCachedContentCards,
      logContentCardImpression,
      setUserId,
      toggleContentCards,
      logContentCardClicked,
      setActiveDayParts,
    ]
  );

  return ctxValue;
};

export default useBraze;

declare global {
  interface Window {
    braze: IBraze;
    rbiBraze?: IBraze;
    rbiBrazeInit?: () => void;
  }
}
